import { queryClient } from "./queryClient";

type RealtimeEvent =
  | { type: "notification"; kind: string; notificationId?: string | null }
  | { type: "data"; topic: string; ids?: Record<string, unknown> | null }
  | { type: "ping" };

type Listener = (ev: RealtimeEvent) => void;

// Server sends a heartbeat every 25s. If we haven't seen anything (heartbeat
// or real event) in this window, the connection is presumed half-open
// (typical after mobile sleep / network handoff) and is force-reconnected.
const STALE_AFTER_MS = 60_000;
const WATCHDOG_INTERVAL_MS = 15_000;
// On resume-from-background, treat the stream as stale if the last frame
// is older than this — anything fresher than this and the stream is fine.
const RESUME_STALE_THRESHOLD_MS = 30_000;

const listeners = new Set<Listener>();
let started = false;
let es: EventSource | null = null;
let lastFrameAt = 0;
let watchdogTimer: ReturnType<typeof setInterval> | null = null;
let onSwMessageRef: ((e: MessageEvent) => void) | null = null;
let onVisibleRef: (() => void) | null = null;
let onFocusRef: (() => void) | null = null;

const TOPIC_QUERY_PREFIXES: Record<string, string[]> = {
  assignments: [
    "/api/assignments",
    "/api/dashboard/assignments",
    "/api/dashboard/messages",
  ],
  messages: [
    "/api/messages",
    "/api/sponsor/contact-requests",
    "/api/dashboard/messages",
  ],
  notifications: [
    "/api/notifications",
    "/api/dashboard/messages",
  ],
  hollas: [
    "/api/home-group/hollas",
    "/api/home-group/my-hollas",
    "/api/dashboard/messages",
  ],
  gratitude: [
    "/api/gratitude-lists",
    "/api/sponsor/sponsees",
  ],
  announcements: [
    "/api/home-group/announcements",
    "/api/dashboard/messages",
  ],
  encouragements: [
    "/api/home-group/encouragements",
    "/api/home-group/my-encouragements",
    "/api/dashboard/messages",
  ],
  burning_desire: [
    "/api/burning-desire",
    "/api/dashboard/messages",
  ],
  crisis_plans: [
    "/api/crisis-plans",
    "/api/triggers",
  ],
  stepwork_documents: [
    "/api/stepwork-documents",
    "/api/dashboard/messages",
  ],
  step_progress: [
    "/api/steps",
    "/api/progress",
    "/api/work",
    "/api/sponsor/sponsees",
    "/api/sponsor/submissions",
    "/api/sponsor/pending-graduations",
    "/api/sponsorship/family-tree",
    "/api/sponsorship",
    "/api/dashboard/messages",
  ],
  relationships: [
    "/api/relationship-requests",
    "/api/sponsor/sponsees",
    "/api/sponsor/relationships",
    "/api/sponsee/sponsor",
    "/api/sponsorship/family-tree",
    "/api/auth/user",
  ],
  contact_requests: [
    "/api/sponsor/contact-requests",
    "/api/sponsors",
    "/api/messages",
  ],
  inner_circle: [
    "/api/inner-circle",
  ],
};

// `kind` here is the *top-level* NotificationKind on the SSE frame
// (see server/notify.ts). The "system" bucket is intentionally a catch-all
// because the inner sub-kind (new_assignment, submitted_for_review,
// home_group_announcement, …) isn't carried on the SSE frame.
const KIND_TOPICS: Record<string, string[]> = {
  system: [
    "assignments",
    "step_progress",
    "announcements",
    "stepwork_documents",
  ],
  contact_message: ["messages", "contact_requests"],
  contact_request: ["contact_requests"],
  contact_request_decline: ["contact_requests", "messages"],
  contact_info_request: ["contact_requests", "messages"],
  relationship_request: ["relationships"],
  burning_desire: ["burning_desire"],
  crisis_plan_yearly_review: ["crisis_plans"],
  inner_circle_invite: ["inner_circle"],
  inner_circle_post: ["inner_circle"],
  inner_circle_accept: ["inner_circle"],
  inner_circle_decline: ["inner_circle"],
  peer_encouragement: ["encouragements"],
};

const ALL_TOPICS = Object.keys(TOPIC_QUERY_PREFIXES);

/**
 * Catch-up refresh: re-pull every realtime-managed topic. Used on reconnect,
 * tab focus, and visibility-change so any data events that may have been
 * missed during a network blip / sleep / backgrounded tab are recovered.
 */
function catchUpRefresh(): void {
  ALL_TOPICS.forEach(invalidateTopic);
  refreshAlwaysOn();
}

function invalidateTopic(topic: string): void {
  const prefixes = TOPIC_QUERY_PREFIXES[topic];
  if (!prefixes || prefixes.length === 0) {
    queryClient.invalidateQueries({ queryKey: ["/api/dashboard/messages"] });
    return;
  }
  queryClient.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey[0];
      if (typeof k !== "string") return false;
      return prefixes.some((p) => k.startsWith(p));
    },
  });
}

function refreshAlwaysOn(): void {
  // Pulled by the bell, the carousel, and many dashboards.
  queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
  queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
  queryClient.invalidateQueries({ queryKey: ["/api/dashboard/messages"] });
}

function handleEvent(ev: RealtimeEvent): void {
  if (ev.type === "data") {
    invalidateTopic(ev.topic);
  } else if (ev.type === "notification") {
    const topics = KIND_TOPICS[ev.kind];
    if (topics) topics.forEach(invalidateTopic);
  }
  // Always nudge the bell + dashboard summary on any inbound event.
  refreshAlwaysOn();
  listeners.forEach((fn) => {
    try {
      fn(ev);
    } catch {
      /* swallow listener errors */
    }
  });
}

function connect(): void {
  if (es) return;
  try {
    es = new EventSource("/api/notifications/stream", { withCredentials: true });
    lastFrameAt = Date.now();
    es.onopen = () => {
      lastFrameAt = Date.now();
    };
    es.onmessage = (msg) => {
      // Any inbound frame — including server heartbeats — proves the socket
      // is still alive. Bump the watchdog timestamp before parsing.
      lastFrameAt = Date.now();
      try {
        const parsed = JSON.parse(msg.data);
        if (!parsed || typeof parsed !== "object") return;
        if (parsed.type === "ping") return;
        if (parsed.type === "data" || parsed.type === "notification") {
          handleEvent(parsed as RealtimeEvent);
        }
      } catch {
        /* ignore malformed frames */
      }
    };
    es.onerror = () => {
      // EventSource auto-reconnects; do a full catch-up refresh so any data
      // events missed during the disconnect are recovered.
      catchUpRefresh();
    };
  } catch {
    /* EventSource unavailable — fall back to push + visibility triggers */
  }
}

// Cooldown so simultaneous triggers (watchdog + visibility + focus firing in
// the same tick on resume) collapse into a single reconnect.
let lastReconnectAt = 0;
const RECONNECT_COOLDOWN_MS = 5_000;

/**
 * Tear down the current EventSource and open a fresh one. Used when the
 * watchdog or a resume-from-background trigger detects the stream is stale
 * (half-open after mobile sleep, network handoff, etc.). No-op if realtime
 * has been stopped, or if a reconnect just happened.
 */
function forceReconnect(): void {
  if (!started) return;
  if (Date.now() - lastReconnectAt < RECONNECT_COOLDOWN_MS) return;
  lastReconnectAt = Date.now();
  if (es) {
    try {
      es.close();
    } catch {
      /* ignore */
    }
    es = null;
  }
  connect();
  catchUpRefresh();
}

function isStale(thresholdMs: number): boolean {
  return Date.now() - lastFrameAt > thresholdMs;
}

/**
 * Start the global realtime client. Safe to call multiple times — only the
 * first call has any effect. Should be invoked once after the user is
 * authenticated, and torn down via stopRealtime() on logout.
 */
export function startRealtime(): void {
  if (started) return;
  started = true;

  connect();

  onSwMessageRef = (e: MessageEvent) => {
    // Push messages indicate something happened server-side — catch up fully
    // because the page's queries default to staleTime: Infinity and won't
    // refetch otherwise.
    if (e.data && e.data.type === "push") catchUpRefresh();
  };
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", onSwMessageRef);
  }

  onVisibleRef = () => {
    if (document.visibilityState !== "visible") return;
    // Returning from a backgrounded tab / locked phone: if we haven't seen
    // a heartbeat recently, the underlying socket is almost certainly dead.
    // Force a reconnect rather than waiting for the browser to notice.
    if (isStale(RESUME_STALE_THRESHOLD_MS)) {
      forceReconnect();
    } else {
      catchUpRefresh();
    }
  };
  onFocusRef = () => {
    if (isStale(RESUME_STALE_THRESHOLD_MS)) {
      forceReconnect();
    } else {
      catchUpRefresh();
    }
  };
  document.addEventListener("visibilitychange", onVisibleRef);
  window.addEventListener("focus", onFocusRef);

  // Watchdog: server sends a `data: {"type":"ping"}` heartbeat every 25s.
  // If we go a full minute without any frame, presume the socket is half-open
  // (typical after mobile sleep or a network handoff) and force a reconnect.
  watchdogTimer = setInterval(() => {
    if (document.visibilityState !== "visible") return;
    if (isStale(STALE_AFTER_MS)) forceReconnect();
  }, WATCHDOG_INTERVAL_MS);
}

/**
 * Stop the global realtime client. Idempotent. Tear down on logout / when the
 * user is no longer authenticated, so the next login establishes a fresh
 * EventSource bound to the new session cookie.
 */
export function stopRealtime(): void {
  if (!started) return;
  started = false;

  if (watchdogTimer) {
    clearInterval(watchdogTimer);
    watchdogTimer = null;
  }
  if (es) {
    try {
      es.close();
    } catch {
      /* ignore */
    }
    es = null;
  }
  lastFrameAt = 0;
  lastReconnectAt = 0;
  if (onSwMessageRef && "serviceWorker" in navigator) {
    navigator.serviceWorker.removeEventListener("message", onSwMessageRef);
  }
  if (onVisibleRef) document.removeEventListener("visibilitychange", onVisibleRef);
  if (onFocusRef) window.removeEventListener("focus", onFocusRef);
  onSwMessageRef = null;
  onVisibleRef = null;
  onFocusRef = null;
}

export function subscribeRealtime(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
